import React, { FormEvent, useEffect, useMemo, useState } from "react";
import { Button, Card, Col, Form, Modal, Row, Table } from "react-bootstrap";
import { useSelector } from "react-redux";
import Breadcrumbs from "../../Common/Breadcrumbs";
import ShimmerLoader from "../../Common/ShimmerLoader";
import {
  createSport,
  deleteSport,
  listSports,
  updateSport,
} from "../../api/sportsManagement/sportsService";
import { Sport, SportModuleName, SportPayload } from "../../api/sportsManagement/types";
import { hasAnyPermission } from "../../utils/roleBasedAccess";
import AssetUploadField from "../../components/tournament/AssetUploadField";

const PRODUCT_MODULES = ["tournament", "academy", "shop", "payAndPlay", "playerVault"];

const emptySport = (): SportPayload => ({
  name: "",
  shortName: "",
  code: "",
  slug: "",
  isTeamSport: true,
  isActive: true,
  iconUrl: "",
  modules: Object.fromEntries(
    PRODUCT_MODULES.map((module) => [module, { enabled: false }])
  ),
  visibility: {
    showOnWebsiteLeadsForm: false,
    showOnAppFilters: false,
    showOnWebsite: false,
  },
});

const SportForm: React.FC<{
  value: SportPayload;
  onChange: (value: SportPayload) => void;
}> = ({ value, onChange }) => {
  const update = (patch: Partial<SportPayload>) => onChange({ ...value, ...patch });
  const updateModule = (module: SportModuleName, patch: Record<string, any>) =>
    update({
      modules: {
        ...value.modules,
        [module]: { ...(value.modules[module] || { enabled: false }), ...patch },
      },
    });

  return (
    <div>
      <Row className="g-3">
        {(["name", "shortName", "code", "slug"] as const).map((field) => (
          <Col md={6} key={field}>
            <Form.Label>{field === "shortName" ? "Short name" : field[0].toUpperCase() + field.slice(1)}</Form.Label>
            <Form.Control
              required
              value={value[field]}
              onChange={(event) => update({ [field]: event.target.value } as Partial<SportPayload>)}
            />
          </Col>
        ))}
        <Col md={12}>
          <AssetUploadField
            label="Sport icon"
            value={value.iconUrl}
            context={`sports/${value.slug || "new"}`}
            onChange={(iconUrl) => update({ iconUrl })}
          />
        </Col>
        <Col md={12} className="d-flex gap-4">
          <Form.Check
            type="switch"
            label="Team sport"
            checked={value.isTeamSport}
            onChange={(event) => update({ isTeamSport: event.target.checked })}
          />
          <Form.Check
            type="switch"
            label="Active"
            checked={value.isActive}
            onChange={(event) => update({ isActive: event.target.checked })}
          />
        </Col>
        <Col md={12}>
          <h6>Visibility</h6>
          {Object.entries(value.visibility).map(([key, enabled]) => (
            <Form.Check
              className="mb-2"
              key={key}
              type="switch"
              label={key.replace(/([A-Z])/g, " $1")}
              checked={enabled}
              onChange={(event) =>
                update({ visibility: { ...value.visibility, [key]: event.target.checked } })
              }
            />
          ))}
        </Col>
        <Col md={12}>
          <h6>Product modules</h6>
          {PRODUCT_MODULES.map((module) => {
            const settings = value.modules[module] || { enabled: false };
            return (
              <Card className="mb-2" key={module}>
                <Card.Body>
                  <Form.Check
                    className="mb-2"
                    type="switch"
                    label={module}
                    checked={settings.enabled}
                    onChange={(event) => updateModule(module, { enabled: event.target.checked })}
                  />
                  <Row className="g-2">
                    {(["iconUrl", "heroImage", "titleImage"] as const).map((field) => (
                      <Col md={4} key={field}>
                        <AssetUploadField
                          label={field.replace(/([A-Z])/g, " $1")}
                          value={settings[field] || ""}
                          context={`sports/${value.slug || "new"}/${module}`}
                          onChange={(assetUrl) => updateModule(module, { [field]: assetUrl })}
                        />
                      </Col>
                    ))}
                  </Row>
                </Card.Body>
              </Card>
            );
          })}
        </Col>
      </Row>
    </div>
  );
};

const SportsManagement = () => {
  const { user, role, permissions } = useSelector((state: any) => state.Login);
  const canManageSports = hasAnyPermission(
    { ...(user || {}), subRole: role, permissions },
    ["sports_management.manage"]
  );
  const [sports, setSports] = useState<Sport[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [search, setSearch] = useState("");
  const [editedSport, setEditedSport] = useState<SportPayload>(emptySport());
  const [selectedSport, setSelectedSport] = useState<Sport | null>(null);
  const [showEditor, setShowEditor] = useState(false);
  const [saving, setSaving] = useState(false);

  const loadSports = async () => {
    setLoading(true);
    try {
      setSports(await listSports());
      setError("");
    } catch (err: any) {
      setError(err?.message || "Unable to load sports.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadSports();
  }, []);

  const filteredSports = useMemo(
    () =>
      sports.filter((sport) =>
        [sport.name, sport.code, sport.shortName].some((value) =>
          value.toLowerCase().includes(search.toLowerCase())
        )
      ),
    [search, sports]
  );

  const openCreate = () => {
    setSelectedSport(null);
    setEditedSport(emptySport());
    setShowEditor(true);
  };

  const openEdit = (sport: Sport) => {
    const { _id, createdAt, updatedAt, ...payload } = sport;
    setSelectedSport(sport);
    setEditedSport(payload);
    setShowEditor(true);
  };

  const save = async (event: FormEvent) => {
    event.preventDefault();
    setSaving(true);
    try {
      if (selectedSport) {
        await updateSport(selectedSport._id, selectedSport, editedSport);
      } else {
        await createSport(editedSport);
      }
      setShowEditor(false);
      await loadSports();
    } catch (err: any) {
      setError(err?.message || "Unable to save sport.");
    } finally {
      setSaving(false);
    }
  };

  const remove = async (sport: Sport) => {
    if (!window.confirm(`Delete ${sport.name}? This cannot be undone.`)) return;
    try {
      await deleteSport(sport._id);
      await loadSports();
    } catch (err: any) {
      setError(err?.message || "Unable to delete sport.");
    }
  };

  return (
    <div className="page-content">
      <div className="container-fluid">
        <Breadcrumbs items={[{ label: "Sports Management", active: true }]} />
        <div className="d-flex justify-content-between align-items-center my-4">
          <div>
            <h1 className="page-title mb-1">Sports Management</h1>
            <p className="text-muted mb-0">Manage sports available across SFL products.</p>
          </div>
          {canManageSports && <Button onClick={openCreate}><i className="ph-plus me-1" />Add sport</Button>}
        </div>
        {error && <div className="alert alert-danger">{error}</div>}
        <Card>
          <Card.Body>
            <Form.Control
              className="mb-3"
              placeholder="Search by sport name, code, or short name"
              value={search}
              onChange={(event) => setSearch(event.target.value)}
            />
            {loading ? <ShimmerLoader type="table" /> : (
              <Table responsive hover>
                <thead><tr><th>Sport</th><th>Code</th><th>Format</th><th>Active</th><th>Tournament</th><th /></tr></thead>
                <tbody>
                  {filteredSports.map((sport) => (
                    <tr key={sport._id}>
                      <td>{sport.name}<small className="d-block text-muted">{sport.shortName}</small></td>
                      <td>{sport.code}</td>
                      <td>{sport.isTeamSport ? "Team" : "Individual"}</td>
                      <td>{sport.isActive ? "Yes" : "No"}</td>
                      <td>{sport.modules.tournament?.enabled ? "Enabled" : "Disabled"}</td>
                      <td className="text-end">
                        {canManageSports && <>
                          <Button variant="light" size="sm" onClick={() => openEdit(sport)}>Edit</Button>{" "}
                          <Button variant="outline-danger" size="sm" onClick={() => remove(sport)}>Delete</Button>
                        </>}
                      </td>
                    </tr>
                  ))}
                  {!filteredSports.length && <tr><td colSpan={6} className="text-center text-muted py-4">No sports found.</td></tr>}
                </tbody>
              </Table>
            )}
          </Card.Body>
        </Card>
      </div>
      <Modal show={showEditor} onHide={() => setShowEditor(false)} size="xl">
        <Form onSubmit={save}>
          <Modal.Header closeButton><Modal.Title>{selectedSport ? "Edit sport" : "Add sport"}</Modal.Title></Modal.Header>
          <Modal.Body><SportForm value={editedSport} onChange={setEditedSport} /></Modal.Body>
          <Modal.Footer>
            <Button variant="light" onClick={() => setShowEditor(false)}>Cancel</Button>
            <Button type="submit" disabled={saving}>{saving ? "Saving..." : "Save sport"}</Button>
          </Modal.Footer>
        </Form>
      </Modal>
    </div>
  );
};

export default SportsManagement;
